import React from 'react'
import SEO from './SEO'
import './../styles/PrivacyPolicy.css'

const PrivacyPolicy = () => (
  <>
    <SEO
      path="/privacy-policy"
      title="Privacy Policy"
      description="Read QA Tech's privacy policy to understand how we collect, use and protect your personal data."
      keywords="QA Tech privacy policy, data protection, GDPR, privacy"
    />
    <div className="privacy-policy-container">
      <h1>Privacy Policy</h1>
      <p>Last updated: 4 May 2026</p>
      <p>
        QA Tech Ltd ("we", "our", "us") is committed to protecting and respecting your privacy. This Privacy Policy
        explains how we collect, use, disclose, and safeguard your information when you visit our website , use our
        services, or engage with us in other ways. Please read this policy carefully to understand our views and
        practices regarding your personal data and how we will treat it.
      </p>

      <h2>1. Information We Collect</h2>
      <p>We may collect and process the following data about you:</p>
      <ul>
        <li>
          <strong>Personal Identification Information:</strong> Name, Email address, Company name, Phone number, Address
        </li>
        <li>
          <strong>Technical Data:</strong> Internet Protocol (IP) address, Browser type and version, Time zone setting,
          Browser plug-in types and versions, Operating system and platform
        </li>
        <li>
          <strong>Usage Data:</strong> Information about how you use our website and services
        </li>
        <li>
          <strong>Marketing and Communications Data:</strong> Your preferences in receiving marketing from us and your
          communication preferences
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect in the following ways:</p>
      <ul>
        <li>To provide, operate, and maintain our website and services</li>
        <li>To improve, personalize, and expand our website and services</li>
        <li>To understand and analyze how you use our website and services</li>
        <li>To develop new products, services, features, and functionality</li>
        <li>
          To communicate with you, either directly or through one of our partners, including for customer service, to
          provide you with updates and other information relating to the website, and for marketing and promotional
          purposes
        </li>
        <li>To process your transactions and manage your orders</li>
        <li>To send you emails</li>
        <li>To find and prevent fraud</li>
        <li>To comply with legal obligations</li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>
        We do not sell, trade, or otherwise transfer to outside parties your Personally Identifiable Information unless
        we provide users with advance notice. This does not include website hosting partners and other parties who
        assist us in operating our website, conducting our business, or serving our users, so long as those parties
        agree to keep this information confidential. We may also release information when its release is appropriate to
        comply with the law, enforce our site policies, or protect ours or others' rights, property, or safety.
      </p>

      <h2>4. Cookies, Analytics &amp; Tracking</h2>
      <p>
        We use <strong>Google Analytics 4</strong> (provided by Google Ireland
        Limited / Google LLC) to understand how visitors use our website. This
        helps us improve the experience and measure the effectiveness of our
        content. Our analytics setup is configured with privacy in mind:
      </p>
      <ul>
        <li>
          <strong>Advertising signals are disabled.</strong> We have switched
          off Google Signals, ad personalisation, and the storage of any
          advertising-related data (<code>ad_storage</code>,{' '}
          <code>ad_user_data</code>, <code>ad_personalization</code>).
        </li>
        <li>
          <strong>No remarketing or ad cookies.</strong> We do not use Google
          Analytics data to build advertising audiences or target you with ads
          on other sites.
        </li>
        <li>
          <strong>IP anonymisation is enabled</strong> and Google Analytics 4
          does not record full IP addresses.
        </li>
        <li>
          <strong>What we collect:</strong> page views, route changes,
          approximate geographic region, device and browser type, referrer,
          and engagement events such as clicks on key call-to-action buttons,
          contact form submissions, outbound link clicks, and email link
          clicks.
        </li>
        <li>
          <strong>What we do not collect through analytics:</strong> your
          name, email address or any information you type into a form is{' '}
          <em>not</em> sent to Google Analytics. Form contents are only sent
          to our form processor (Web3Forms) when you press submit.
        </li>
      </ul>
      <p>
        Our legal basis for this processing is our <strong>legitimate
        interest</strong> in understanding how our website is used and
        improving it. Because no advertising or marketing cookies are set,
        the processing is low-impact and does not require advertising consent
        under UK GDPR / PECR. If you would prefer we did not record your
        visit, you can install a browser extension that blocks Google
        Analytics, use your browser&rsquo;s &ldquo;Do Not Track&rdquo;
        setting, or contact us at{' '}
        <a href="mailto:info@qatechltd.co.uk">info@qatechltd.co.uk</a> and we
        will exclude your data on request.
      </p>
      <p>
        Google may transfer the data it collects on our behalf outside the
        UK / EEA. Where this happens, Google relies on Standard Contractual
        Clauses approved by the European Commission and the UK Information
        Commissioner&rsquo;s Office. Google&rsquo;s own privacy policy is
        available at{' '}
        <a
          href="https://policies.google.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
        >
          policies.google.com/privacy
        </a>
        .
      </p>

      <h2>5. Security of Your Information</h2>
      <p>
        We use administrative, technical, and physical security measures to help protect your personal information.
        While we have taken reasonable steps to secure the personal information you provide to us, please be aware that
        despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be
        guaranteed against any interception or other type of misuse.
      </p>

      <h2>6. Your Data Protection Rights</h2>
      <p>Depending on your location, you may have the following rights regarding your personal data:</p>
      <ul>
        <li>The right to access – You have the right to request copies of your personal data.</li>
        <li>
          The right to rectification – You have the right to request that we correct any information you believe is
          inaccurate. You also have the right to request that we complete the information you believe is incomplete.
        </li>
        <li>
          The right to erasure – You have the right to request that we erase your personal data, under certain
          conditions.
        </li>
        <li>
          The right to restrict processing – You have the right to request that we restrict the processing of your
          personal data, under certain conditions.
        </li>
        <li>
          The right to object to processing – You have the right to object to our processing of your personal data,
          under certain conditions.
        </li>
        <li>
          The right to data portability – You have the right to request that we transfer the data that we have collected
          to another organization, or directly to you, under certain conditions.
        </li>
      </ul>
      <p>
        If you make a request, we have one month to respond to you. If you would like to exercise any of these rights,
        please contact us at our provided contact information.
      </p>

      <h2>7. Changes to This Privacy Policy</h2>
      <p>
        We may update this privacy policy from time to time in order to reflect, for example, changes to our practices
        or for other operational, legal, or regulatory reasons. We will notify you of any changes by posting the new
        Privacy Policy on this page.
      </p>

      <h2>8. Contact Us</h2>
      <p>If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us:</p>
      <p>
        QA Tech Consultancy Ltd
        <br />
        Email: info@qatechltd.co.uk
        <br />
        London
      </p>
    </div>
  </>
)

export default PrivacyPolicy
